import React from 'react';
import numbro from 'numbro';
import OfferGenerationWizard from './OfferGenerationWizard';
import sendMutation from '../../utils/rest/sendMutation';

const FACILITY_TYPE_2_FACILITY_DATA_KEY = {
  wind: 'windFacilityData',
  wasser: 'waterFacilityData',
  photovoltaik: 'solarFacilityData',
  biomasse: 'bioMasseFacilityData',
};

const FACILITY_TYPE_2_NUMBER_FIELDS = {
  wind: [
    'hubHeight',
    'diameter',
    'output',
    'powerDensity',
    'latitude',
    'longitude',
  ],
  wasser: ['output', 'generated', 'latitude', 'longitude'],
  photovoltaik: ['output', 'generated', 'latitude', 'longitude'],
  biomasse: ['generated', 'latitude', 'longitude'],
};

/**
 * Unformats the number entries in an object
 * @param {object} obj - the object whose numbers should be unformatted
 * @param {array} keys - list of the object's keys that should be unformatted
 * @returns {object} - the object but with unformatted number entries
 */
function unformatNumbers(obj, keys) {
  const o = { ...obj };
  keys.forEach((k) => {
    if (o[k] && typeof o[k] === 'string' && o[k].length > 0) {
      // TODO: send the plain number if the middleware allows it
      o[k] = String(numbro.unformat(o[k]));
    } else {
      o[k] = null;
    }
  });
  return o;
}

/**
 * Converts a string to a boolean
 * @param {string} string 'true' or 'false'
 * @returns {boolean|null} true or false
 */
function convertStringToBoolean(string) {
  if (string === 'true') {
    return true;
  }
  if (string === 'false') {
    return false;
  }
  return null;
}

function OfferGenerationWizardContainer() {
  const prepareFacilityData = (facilityType, values) => {
    const facilityKey = FACILITY_TYPE_2_FACILITY_DATA_KEY[facilityType];
    const numberFields = FACILITY_TYPE_2_NUMBER_FIELDS[facilityType];
    if (!facilityKey) {
      return null;
    }
    const facilities = values[facilityKey];
    const preparedFacilities = facilities.map((facility) => {
      const newFacility = { ...facility };
      if (facility.gridOperator) {
        newFacility.gridOperator = {
          mastrNumber: facility.gridOperator.id,
          name: facility.gridOperator.name,
        };
      }
      if (facility.country) {
        newFacility.country = facility.country.id;
      }
      delete newFacility.id;
      return unformatNumbers(newFacility, numberFields);
    });
    return preparedFacilities;
  };

  const prepareInformationData = (information, removeDates) => {
    const result = { ...information };
    const country = information.country.id;
    result.country = country;

    if (removeDates) {
      delete result.start;
      delete result.end;
    }
    delete result.file;
    return result;
  };

  const prepareContactData = (contactData) => {
    const { salutation } = contactData;
    let salutationForMiddleware;
    if (
      salutation === undefined ||
      salutation === null ||
      salutation.id === 'notSpecified'
    ) {
      salutationForMiddleware = null;
    } else {
      salutationForMiddleware = salutation.id;
    }

    return {
      ...contactData,
      salutation: salutationForMiddleware,
    };
  };

  /**
   * Submit the user entered wizard values to the middleware
   * @param {Object} values - Form values entered by user
   * @returns {} If an error occurred, the error gets returned
   */
  const handleRequestOffer = async (values) => {
    const removeDates =
      values.product === 'pvGrossanlage' ||
      values.product === 'businessCharging';
    let result;
    try {
      const payload = {
        category: values.category,
        product: values.product,
        facilityType: values.facilityType,
        facilityData: prepareFacilityData(values.facilityType, values),
        information: prepareInformationData(values.information, removeDates),
        contactInformation: prepareContactData(values.individualOffer),
      };
      switch (values.product) {
        case 'zertifikate': {
          payload.certificate = values.certificate;
          switch (values.certificate) {
            case 'co2': {
              payload.co2CertificateData = unformatNumbers(
                values.co2CertificateData,
                ['consumption']
              );
              break;
            }
            case 'proofOfOrigin': {
              payload.proofOfOriginCertificateData = unformatNumbers(
                values.proofOfOriginCertificateData,
                ['consumption']
              );
              break;
            }
            default:
              break;
          }
          break;
        }
        case 'pvGrossanlage': {
          payload.largeSolarFacilityData = {
            location: values.largeSolarFacilityType,
            sizeOfArea: values.largeSolarFacilityData.sizeOfArea,
            consumption: values.largeSolarFacilityData.consumption,
          };
          payload.largeSolarFacilityData = unformatNumbers(
            payload.largeSolarFacilityData,
            ['sizeOfArea', 'consumption']
          );
          break;
        }
        case 'stromlieferung': {
          payload.powerType = values.powerType;
          if (values.powerType === 'gruenstrom') {
            payload.greenPowerData = unformatNumbers(values.greenPowerData, [
              'consumption',
            ]);
          }
          if (values.powerType === 'graustrom') {
            payload.greyPowerData = unformatNumbers(values.greyPowerData, [
              'consumption',
            ]);
          }
          break;
        }
        case 'businessCharging': {
          payload.businessChargingData = unformatNumbers(
            values.businessChargingData,
            [
              'numberOfEmployees',
              'numberOfParkingSpaces',
              'numberOfOwnVehicles',
              'numberOfElectricCars',
            ]
          );

          [
            'isOwnParkingLot',
            'hasOwnCarFleet',
            'isCustomerParkingAllowed',
          ].forEach((key) => {
            if (payload.businessChargingData[key]) {
              payload.businessChargingData[key] = convertStringToBoolean(
                payload.businessChargingData[key]
              );
            }
          });
          break;
        }
        case 'immoCharging': {
          payload.product =
            values.immoChargingData.isResidentialOrCommercial === 'residential'
              ? 'immoChargingResidential'
              : 'immoChargingCommercial';

          payload.immoChargingData = {
            product:
              values.immoChargingData.isResidentialOrCommercial ===
              'residential'
                ? 'immoChargingResidential'
                : 'immoChargingCommercial',
            numberOfUnits: values.immoChargingData.numberOfUnits,
            numberOfGarageObjects:
              values.immoChargingData.numberOfGarageObjects,
            numberOfParkingSpaces:
              values.immoChargingData.numberOfParkingSpaces,
          };
          payload.immoChargingData = unformatNumbers(payload.immoChargingData, [
            'numberOfUnits',
            'numberOfGarageObjects',
            'numberOfParkingSpaces',
          ]);
          break;
        }
        case 'coopCharging': {
          payload.coopChargingData = unformatNumbers(values.coopChargingData, [
            'numberOfHotelLocations',
            'numberOfRooms',
            'numberOfParkingSpaces',
          ]);
          break;
        }
        default:
          break;
      }

      const formData = new FormData();
      formData.append('customerInquiry', JSON.stringify(payload));
      if (values.information.file) {
        formData.append('additionalInformationFile', values.information.file);
      }
      if (values.windFacilityFile) {
        formData.append('windFacilityFile', values.windFacilityFile);
      }

      result = await sendMutation('POST', '/customerinquiry', formData);
      if (result.error) {
        return result.error;
      }
      return false;
    } catch (e) {
      return e;
    }
  };

  return <OfferGenerationWizard handleRequestOffer={handleRequestOffer} />;
}

export default OfferGenerationWizardContainer;
